import {
  IonCol,
  IonItem,
  IonList,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
} from "@ionic/react";
import axios, { AxiosRequestConfig } from "axios";
import dayjs from "dayjs";
import parse from "html-react-parser";
import { useEffect, useState } from "react";
import { convertRtfToHtml } from "rtf-to-html-converter";
import useHttp from "../../hooks/httpAdapter";
import { useSession } from "../../hooks/SessionContext";
import {
  EstudiosPacienteEcografias,
  EstudiosPacientesArchivos,
  EstudiosPacientesPatologia,
  InformeLaboratorio,
  Paciente,
} from "../../interfaces";
import "./ViewerEstudiosPaciente.css";

interface ViewerEstudiosPacienteProps {
  paciente: Paciente;
}

const ViewerEstudiosPaciente: React.FC<ViewerEstudiosPacienteProps> = (
  paciente,
) => {
  const [mostrarTreeview, setMostrarTreeview] = useState<
    | "Ecografias"
    | "Laboratorio"
    | "Colpos-Generales"
    | "Histeroscopias"
    | "Patologia"
    | ""
  >("");
  //Apartado Ecografias (ahora usa archivos de carpeta 3)
  const [estudiosEcografiasArchivos, setEstudiosEcografiasArchivos] = useState<
    EstudiosPacientesArchivos[]
  >([]);
  const [estudiosEcografiasDetalle, setEstudiosEcografiasDetalle] = useState<
    EstudiosPacienteEcografias[]
  >([]);
  //Apartado Laboratorio
  const [estudiosLaboratorio, setEstudiosLaboratorio] = useState<
    InformeLaboratorio[]
  >([]);
  //Apartado Esudios
  const [estudiosColposGenerales, setEstudiosColposGenerales] = useState<
    EstudiosPacientesArchivos[]
  >([]);
  const [estudiosHisteroscopias, setEstudiosHisteroscopias] = useState<
    EstudiosPacientesArchivos[]
  >([]);
  const [estudiosPatologia, setEstudiosPatologia] = useState<
    EstudiosPacientesPatologia[]
  >([]);

  const [selectedEcografia, setSelectedEcografia] =
    useState<EstudiosPacientesArchivos | null>(null);
  const [selectedFechaEcografia, setSelectedFechaEcografia] = useState<
    string | null
  >(null);
  const [selectedLaboratorio, setSelectedLaboratorio] =
    useState<InformeLaboratorio | null>(null);
  const [selectedColposGenerales, setSelectedColposGenerales] =
    useState<EstudiosPacientesArchivos | null>(null);
  const [selectedHisteroscopias, setSelectedHisteroscopias] =
    useState<EstudiosPacientesArchivos | null>(null);
  const [selectedFechaHisteroscopias, setSelectedFechaHisteroscopias] =
    useState<string | null>(null);
  const [selectedPatologia, setSelectedPatologia] =
    useState<EstudiosPacientesPatologia | null>(null);
  const [selectedFechaPatologia, setSelectedFechaPatologia] = useState<
    string | null
  >(null);
  type ArchivoPreviewTipo = "pdf" | "img" | "other" | "rtf";
  const [archivoPreview, setArchivoPreview] = useState<{
    url: string;
    tipo: ArchivoPreviewTipo;
    nombre: string;
  } | null>(null);
  const [ecografiaHtml, setEcografiaHtml] = useState<string | null>(null);

  const [cargando, setCargando] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { hcUL, mostrarNotificacion } = useSession();
  const http = useHttp();

  const extractCodigoFromNombreArchivo = (
    nombreArchivo: string,
  ): string | null => {
    if (!nombreArchivo) return null;
    const matchFin = nombreArchivo.match(/(\d+)(?=\.[^.]+$|$)/);
    if (matchFin?.[1]) return matchFin[1];
    const todosLosNumeros = nombreArchivo.match(/\d+/g);
    if (!todosLosNumeros || todosLosNumeros.length === 0) return null;
    return todosLosNumeros[todosLosNumeros.length - 1];
  };

  const normalizeNombreEcografia = (nombre: string): string => {
    if (!nombre) return "";
    return nombre
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\.[^.]+$/, "")
      .replace(/[_-]+/g, " ")
      .replace(/\s+\d+$/, "")
      .replace(/\s+/g, " ")
      .trim()
      .toUpperCase();
  };

  const getApiBases = (): string[] => {
    const bases = [
      localStorage.getItem("urlAxioPP"),
      localStorage.getItem("urlAxioHistorias"),
      "https://apihistorias.cgap.com.ar/api/v2/",
    ]
      .filter((base): base is string => Boolean(base))
      .map((base) => (base.endsWith("/") ? base : `${base}/`));

    return Array.from(new Set(bases));
  };

  const mapDetalleEcografias = (payload: any): EstudiosPacienteEcografias[] => {
    const detalleRaw = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.result)
          ? payload.result
          : [];

    return detalleRaw
      .map((d: any) => ({
        codigo: Number(d?.codigo) || 0,
        nombre: d?.nombre || "",
        documento: d?.documento || "",
        fecha: d?.fecha || "",
      }))
      .filter(
        (detalle: EstudiosPacienteEcografias) =>
          detalle.codigo > 0 ||
          Boolean(detalle.nombre) ||
          Boolean(detalle.documento),
      );
  };

  const normalizeCodigo = (value: unknown): string => {
    if (value === null || value === undefined) return "";
    return String(value).replace(/\D/g, "");
  };

  const buscarDetalleEcografiaPorArchivo = (
    archivoSel: EstudiosPacientesArchivos,
    detalles: EstudiosPacienteEcografias[],
  ): EstudiosPacienteEcografias | null => {
    const codigoArchivo = extractCodigoFromNombreArchivo(archivoSel.nombre);
    const codigoArchivoNormalizado = normalizeCodigo(codigoArchivo);
    const nombreArchivoNormalizado = normalizeNombreEcografia(
      archivoSel.nombre,
    );

    let ecografiaDetalle = codigoArchivoNormalizado
      ? detalles.find(
          (detalle) =>
            normalizeCodigo(detalle.codigo) === codigoArchivoNormalizado,
        )
      : null;

    if (!ecografiaDetalle) {
      ecografiaDetalle =
        detalles.find((detalle) => {
          const nombreDetalleNormalizado = normalizeNombreEcografia(
            detalle.nombre,
          );
          return (
            nombreDetalleNormalizado === nombreArchivoNormalizado ||
            nombreDetalleNormalizado.includes(nombreArchivoNormalizado) ||
            nombreArchivoNormalizado.includes(nombreDetalleNormalizado)
          );
        }) || null;
    }

    if (!ecografiaDetalle && selectedFechaEcografia) {
      ecografiaDetalle =
        detalles.find((detalle) => {
          if (!detalle?.fecha) return false;
          return (
            dayjs(detalle.fecha).format("DD/MM/YYYY") === selectedFechaEcografia
          );
        }) || null;
    }

    if (!ecografiaDetalle && detalles.length === 1) {
      ecografiaDetalle = detalles[0];
    }

    return ecografiaDetalle;
  };

  const obtenerDetalleEcografias = async (
    pacienteId: number,
  ): Promise<EstudiosPacienteEcografias[]> => {
    const bases = getApiBases();
    const endpoints = [
      `EstudiosImagenes/TraerEcografiaPaciente?paciente=${pacienteId}`,
      `EstudiosPacientes/TraerEcografiaPaciente?paciente=${pacienteId}`,
    ];

    const config = {
      headers: { Authorization: `Bearer ${hcUL?.token}` },
    };

    // Priorizar httpAdapter para mantener el mismo flujo/base del portal.
    for (const endpoint of endpoints) {
      try {
        const response = await http.get(endpoint, config);
        const mapped = mapDetalleEcografias(response);
        if (mapped.length > 0) {
          console.log("Detalles ecografias OK (httpAdapter):", {
            endpoint,
            total: mapped.length,
          });
          return mapped;
        }
      } catch (_e) {
        // continuar con fallback
      }
    }

    let detallesFallback: EstudiosPacienteEcografias[] = [];

    for (const base of bases) {
      for (const endpoint of endpoints) {
        const url = `${base}${endpoint}`;
        const intentos: AxiosRequestConfig[] = [
          { headers: { Authorization: `Bearer ${hcUL?.token}` } },
          {},
        ];
        for (const config of intentos) {
          try {
            const resp = await axios.get(url, config);
            const mapped = mapDetalleEcografias(resp?.data);
            if (mapped.length > 0) {
              console.log("Detalles ecografias OK (axios):", {
                url,
                total: mapped.length,
              });
              return mapped;
            }
            if (mapped.length === 0 && detallesFallback.length === 0)
              detallesFallback = mapped;
          } catch (_e) {
            // continuar con siguiente intento/base
          }
        }
      }
    }

    console.warn(
      "No se pudo obtener detalle de ecografías en ningún endpoint/base",
    );
    return detallesFallback;
  };

  // Hook para detectar cambios de tamaño de pantalla
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Nuevo flujo + recuperación de fecha original (usa archivos carpeta 3 y endpoint antiguo para la fecha)
  const cargarEstudioEcografiasPaciente = async (
    pacienteId: number,
    documentoNro: number,
  ) => {
    const config = {
      headers: { Authorization: `Bearer ${hcUL?.token}` },
    };

    // 1) Obtener archivos (nuevo endpoint)
    const archivos: EstudiosPacientesArchivos[] = [];
    const respArchivos = await http.get(
      `EstudiosPacientes/TraerNombresArchivos?documentoNro=${documentoNro}&carpeta=3`,
      config,
    );
    respArchivos.forEach((element: any) => {
      archivos.push({
        fechaCreacion: element.fechaCreacion,
        fechaModificacion: element.fechaModificacion,
        nombre: element.nombre,
        fecha: element.fechaCreacion, // default: fecha de creación del archivo
      });
    });

    // 2) Obtener fechas/documento del endpoint de detalle
    const detalles = await obtenerDetalleEcografias(pacienteId);
    setEstudiosEcografiasDetalle(detalles);

    // 3) Mapear por código de archivo (extraído del nombre) para conservar la fecha original
    // El nombre del archivo nuevo es como: ECOGRAFIA_GINECOLOGICA_TRANSVAGINAL__116313.pdf
    // El nombre del endpoint viejo es como: ECOGRAFIA GINECOLOGICA TRANSVAGINAL
    // El código está al final del nombre del archivo (después de __)
    const mapaFechas = new Map<string, string>();
    detalles.forEach((d: any) => {
      if (d?.codigo && d?.fecha) {
        mapaFechas.set(String(d.codigo), d.fecha);
      }
    });

    console.log("Mapa de fechas por código:", Array.from(mapaFechas.entries()));

    const results = archivos.map((a) => {
      // Extraer el código del nombre del archivo
      // Formato: NOMBRE_CODIGO.extension (el código está después del último _ antes de la extensión)
      const codigo = extractCodigoFromNombreArchivo(a.nombre);

      const fechaOriginal = codigo ? mapaFechas.get(codigo) : null;

      console.log(
        `Archivo: ${a.nombre}, Código extraído: ${codigo}, Fecha encontrada: ${fechaOriginal}`,
      );

      return {
        ...a,
        fecha: fechaOriginal || a.fechaCreacion,
      } as EstudiosPacientesArchivos;
    });

    setEstudiosEcografiasArchivos(results);
  };

  const descargarEcografiaPaciente = async (
    codigoDocumento: number,
    archivoSel: EstudiosPacientesArchivos,
  ) => {
    const configArrayBuffer: AxiosRequestConfig = {
      headers: { Authorization: `Bearer ${hcUL?.token}` },
      responseType: "arraybuffer",
    };
    try {
      let detallesDisponibles = estudiosEcografiasDetalle;
      let ecografiaDetalle = buscarDetalleEcografiaPorArchivo(
        archivoSel,
        detallesDisponibles,
      );

      if (
        (!ecografiaDetalle || !ecografiaDetalle.documento) &&
        paciente.paciente?.codigo
      ) {
        detallesDisponibles = await obtenerDetalleEcografias(
          paciente.paciente.codigo,
        );
        if (detallesDisponibles.length > 0) {
          setEstudiosEcografiasDetalle(detallesDisponibles);
          ecografiaDetalle = buscarDetalleEcografiaPorArchivo(
            archivoSel,
            detallesDisponibles,
          );
        }
      }

      if (ecografiaDetalle?.documento) {
        const htmlDesdeDetalle = convertRtfTextToHtml(
          ecografiaDetalle.documento,
        );
        if (htmlDesdeDetalle) {
          if (archivoPreview && archivoPreview.url) {
            URL.revokeObjectURL(archivoPreview.url);
          }
          setEcografiaHtml(htmlDesdeDetalle);
          setArchivoPreview({
            url: "",
            tipo: "rtf",
            nombre: archivoSel.nombre,
          });
          setSelectedEcografia(archivoSel);
          return;
        }
      }

      let responseData: any = null;
      let responseHeaders: any = null;

      // Priorizar httpAdapter para evitar inconsistencias de base/headers.
      const endpointsLocal = [
        `EstudiosPacientes/DescargarCospoImagenes?documentoNro=${codigoDocumento}&nombreArchivo=${archivoSel.nombre}&carpeta=3`,
        `EstudiosImagenes/DescargarImagenes?documentoNro=${codigoDocumento}&nombreArchivo=${archivoSel.nombre}&carpeta=3`,
      ];
      for (const endpoint of endpointsLocal) {
        try {
          const response = await http.get(endpoint, configArrayBuffer);
          if (response) {
            responseData = response;
            console.log("Descarga ecografia OK (httpAdapter):", { endpoint });
            break;
          }
        } catch (_e) {
          // continuar con fallback
        }
      }

      // Fallback: probar bases explícitas con axios.
      const bases = getApiBases();
      const endpoints = [
        `EstudiosPacientes/DescargarCospoImagenes?documentoNro=${codigoDocumento}&nombreArchivo=${archivoSel.nombre}&carpeta=3`,
        `EstudiosImagenes/DescargarImagenes?documentoNro=${codigoDocumento}&nombreArchivo=${archivoSel.nombre}&carpeta=3`,
      ];

      if (!responseData) {
        for (const base of bases) {
          for (const endpoint of endpoints) {
            const url = `${base}${endpoint}`;
            const intentos: AxiosRequestConfig[] = [
              configArrayBuffer,
              { responseType: "arraybuffer" },
            ];
            for (const cfg of intentos) {
              try {
                const response = await axios.get(url, cfg);
                if (response?.data) {
                  responseData = response.data;
                  responseHeaders = response.headers || null;
                  console.log("Descarga ecografia OK (axios):", { url });
                  break;
                }
              } catch (_e) {
                // continuar
              }
            }
            if (responseData) break;
          }
          if (responseData) break;
        }
      }

      if (!responseData) {
        throw new Error("No se pudo descargar la ecografía desde ninguna base");
      }

      const responseBuffer = await toArrayBuffer(responseData);
      const isPdfBinary = responseBuffer
        ? isPdfArrayBuffer(responseBuffer)
        : false;

      // Solo convertir a texto si NO es un PDF binario real.
      if (responseBuffer && !isPdfBinary) {
        const rawTextUtf8 = new TextDecoder("utf-8", { fatal: false }).decode(
          new Uint8Array(responseBuffer),
        );
        const rawTextLatin1 = new TextDecoder("latin1", {
          fatal: false,
        }).decode(new Uint8Array(responseBuffer));

        const htmlDesdeRtf =
          convertRtfTextToHtml(rawTextUtf8) ||
          convertRtfTextToHtml(rawTextLatin1);
        if (htmlDesdeRtf) {
          if (archivoPreview && archivoPreview.url) {
            URL.revokeObjectURL(archivoPreview.url);
          }
          setEcografiaHtml(htmlDesdeRtf);
          setArchivoPreview({
            url: "",
            tipo: "rtf",
            nombre: archivoSel.nombre,
          });
          setSelectedEcografia(archivoSel);
          return;
        }

        const textoPdf = await extractTextFromPdfArrayBuffer(responseBuffer);
        const htmlDesdePdfTexto = convertRtfTextToHtml(textoPdf);
        if (htmlDesdePdfTexto) {
          if (archivoPreview && archivoPreview.url) {
            URL.revokeObjectURL(archivoPreview.url);
          }
          setEcografiaHtml(htmlDesdePdfTexto);
          setArchivoPreview({
            url: "",
            tipo: "rtf",
            nombre: archivoSel.nombre,
          });
          setSelectedEcografia(archivoSel);
          return;
        }

        if (isLikelyReadableText(textoPdf)) {
          const fallbackTexto = fallbackRtfToHtml(textoPdf);
          if (fallbackTexto) {
            if (archivoPreview && archivoPreview.url) {
              URL.revokeObjectURL(archivoPreview.url);
            }
            setEcografiaHtml(
              `<div style="white-space:normal;font-family:Arial,sans-serif;">${fallbackTexto}</div>`,
            );
            setArchivoPreview({
              url: "",
              tipo: "rtf",
              nombre: archivoSel.nombre,
            });
            setSelectedEcografia(archivoSel);
            return;
          }
        }
      }

      const ext =
        (archivoSel.nombre || "").split(".").pop()?.toLowerCase() || "";
      const defaultContentType =
        isPdfBinary || ext === "pdf"
          ? "application/pdf"
          : ["jpg", "jpeg"].includes(ext)
            ? "image/jpeg"
            : ext === "png"
              ? "image/png"
              : ext === "bmp"
                ? "image/bmp"
                : ext === "gif"
                  ? "image/gif"
                  : "application/octet-stream";
      const contentType =
        responseHeaders?.["content-type"] || defaultContentType;
      const blob =
        responseData instanceof Blob
          ? responseData
          : responseBuffer
            ? new Blob([responseBuffer], { type: contentType })
            : null;
      if (blob && blob.size > 0) {
        if (archivoPreview && archivoPreview.url) {
          URL.revokeObjectURL(archivoPreview.url);
        }
        setEcografiaHtml(null);
        const fileURL = URL.createObjectURL(blob);
        const tipo =
          ext === "pdf"
            ? "pdf"
            : ["jpg", "jpeg", "png", "bmp", "gif"].includes(ext)
              ? "img"
              : "other";

        setArchivoPreview({ url: fileURL, tipo, nombre: archivoSel.nombre });
        setSelectedEcografia(archivoSel);
      } else {
        setSelectedEcografia(archivoSel);
        setArchivoPreview(null);
        setEcografiaHtml(null);
        console.warn(
          "Descarga ecografía sin contenido utilizable:",
          responseData,
        );
      }
    } catch (e) {
      console.error("Error descargando ecografia:", e);
      setSelectedEcografia(archivoSel);
      setArchivoPreview(null);
      setEcografiaHtml(null);
    }
  };
  const cargarEstudioLaboratorioPaciente = async (
    DocumentoPaciente: number,
  ) => {
    try {
      // Limpiar estado previo
      setEstudiosLaboratorio([]);
      setSelectedLaboratorio(null);

      if (DocumentoPaciente) {
        const responseInformes = await axios.get(
          `http://181.105.178.225:4441/gateway/integracion-api/v1/report?DocumentTypeId=1&DocumentNumber=${DocumentoPaciente}`,
        );

        if (responseInformes.data.estado.toUpperCase() === "OK") {
          if (
            responseInformes.data.informes &&
            responseInformes.data.informes.length > 0
          ) {
            // Filtrar informes duplicados basándose en el base64
            const informesUnicos: any[] = [];
            const base64Vistos = new Set();

            responseInformes.data.informes.forEach(
              (informe: any, index: number) => {
                // Usar los primeros 100 caracteres del base64 como identificador único
                const identificadorBase64 = informe.informeBase64
                  ? informe.informeBase64.substring(0, 100)
                  : "";

                if (
                  !base64Vistos.has(identificadorBase64) &&
                  informe.informeBase64
                ) {
                  base64Vistos.add(identificadorBase64);
                  informesUnicos.push(informe);
                }
              },
            );

            const informes = informesUnicos.map((informe: any) => {
              // Parsear fecha en formato ddmmaaaa
              const fechaStr = informe.fecha || "";
              let fechaFormateada = "Fecha no disponible";

              if (fechaStr.length === 8) {
                const dia = fechaStr.substr(0, 2);
                const mes = fechaStr.substr(2, 2);
                const anio = fechaStr.substr(4, 4);
                const fecha = dayjs(`${anio}-${mes}-${dia}`);

                if (fecha.isValid()) {
                  fechaFormateada = fecha.format("DD/MM/YYYY");
                }
              }

              return {
                informeBase64: informe.informeBase64 || "",
                extension: informe.extension || "pdf",
                fecha: fechaFormateada,
              };
            });

            setEstudiosLaboratorio(informes);
            if (informes.length > 0) {
              setSelectedLaboratorio(informes[0]);
            }
          }
        } else {
          console.error(
            "Error en la respuesta de laboratorio:",
            responseInformes.data.mensaje,
          );
        }
      }
    } catch (e) {
      console.error("Error al cargar estudios de laboratorio:", e);
    }
  };
  const cargarEstudiosHisteroscopiasPaciente = async (
    DocumentoPaciente: number,
  ) => {
    const config = {
      headers: { Authorization: `Bearer ${hcUL?.token}` },
    };
    const results: EstudiosPacientesArchivos[] = [];

    const response = await http.get(
      `EstudiosPacientes/TraerNombresArchivos?documentoNro=${DocumentoPaciente}&carpeta=2`,
      config,
    );

    response.forEach((element: any) => {
      results.push({
        fechaCreacion: element.fechaCreacion,
        fechaModificacion: element.fechaModificacion,
        nombre: element.nombre,
        fecha: element.fechaCreacion, // Usar fechaCreacion como fecha para la navegación
      });
    });

    setEstudiosHisteroscopias(results);
  };
  const cargarEstudiosColposGeneralesPaciente = async (
    DocumentoPaciente: number,
  ) => {
    const config = {
      headers: { Authorization: `Bearer ${hcUL?.token}` },
    };
    const results: EstudiosPacientesArchivos[] = [];

    const response = await http.get(
      `EstudiosPacientes/TraerNombresArchivos?documentoNro=${DocumentoPaciente}&carpeta=1`,
      config,
    );

    response.forEach((element: any) => {
      results.push({
        fechaCreacion: element.fechaCreacion,
        fechaModificacion: element.fechaModificacion,
        nombre: element.nombre,
      });
    });

    setEstudiosColposGenerales(results);
  };

  const cargarEstudiosPatologiaPaciente = async (DocumentoPaciente: number) => {
    const config = {
      headers: { Authorization: `Bearer ${hcUL?.token}` },
    };
    const results: EstudiosPacientesPatologia[] = [];

    const response = await http.get(
      `EstudiosPacientes/TraerPatologia?DocumentoPaciente=${DocumentoPaciente}`,
      config,
    );

    response.forEach((element: any) => {
      results.push({
        codigo: element.codigo,
        nombre: element.nombre,
        fecha: element.fecha, // Agregar el campo fecha
      });
    });

    setEstudiosPatologia(results);
  };

  const cargarTodosLosEstudiosDelPaciente = async () => {
    setCargando(true);

    // Limpiar todos los estados antes de cargar nuevos datos
    setEstudiosEcografiasArchivos([]);
    setEstudiosEcografiasDetalle([]);
    setEstudiosLaboratorio([]);
    setEstudiosColposGenerales([]);
    setEstudiosHisteroscopias([]);
    setEstudiosPatologia([]);

    // Limpiar selecciones
    setSelectedEcografia(null);
    setSelectedFechaEcografia(null);
    setSelectedLaboratorio(null);
    setSelectedColposGenerales(null);
    setSelectedHisteroscopias(null);
    setSelectedFechaHisteroscopias(null);
    setSelectedPatologia(null);
    setSelectedFechaPatologia(null);
    setArchivoPreview(null);
    setEcografiaHtml(null);

    const pacienteId = paciente.paciente.codigo || 0;
    const documentoNro = paciente.paciente.documentoNro || 0;

    await cargarEstudioEcografiasPaciente(pacienteId, documentoNro);
    await cargarEstudioLaboratorioPaciente(documentoNro);
    await cargarEstudiosHisteroscopiasPaciente(documentoNro);
    await cargarEstudiosColposGeneralesPaciente(documentoNro);
    await cargarEstudiosPatologiaPaciente(documentoNro);

    setCargando(false);
  };

  useEffect(() => {
    if (paciente.paciente && paciente.paciente.codigo) {
      cargarTodosLosEstudiosDelPaciente();
    }
  }, [paciente.paciente?.codigo]); // Solo recargar cuando cambie el código del paciente

  useEffect(() => {
    return () => {
      if (archivoPreview && archivoPreview.url) {
        try {
          URL.revokeObjectURL(archivoPreview.url);
        } catch (e) {
          // ignore
        }
      }
    };
  }, [archivoPreview]);

  // Función para abrir contenido en nueva ventana/pestaña
  const abrirEnNuevaVentana = (src: string, tipo: string, titulo?: string) => {
    if (tipo === "pdf") {
      // Para PDFs, crear una página HTML que use object/embed para mejor compatibilidad
      const ventana = window.open("", "_blank");
      if (ventana) {
        ventana.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>${titulo || "Documento PDF"}</title>
              <meta charset="UTF-8">
              <style>
                body { 
                  margin: 0; 
                  padding: 0; 
                  height: 100vh; 
                  overflow: hidden;
                  background: #333;
                }
                .pdf-container { 
                  width: 100%; 
                  height: 100vh; 
                  display: flex;
                  flex-direction: column;
                  background: #333;
                }
                .pdf-header {
                  background: #2c2c2c;
                  color: white;
                  padding: 10px 20px;
                  text-align: center;
                  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                  font-size: 14px;
                  border-bottom: 1px solid #555;
                }
                .pdf-viewer { 
                  flex: 1;
                  border: none;
                  width: 100%;
                  background: white;
                }
                .loading {
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  height: 200px;
                  color: white;
                  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                }
                .fallback {
                  text-align: center;
                  padding: 40px 20px;
                  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                  background: white;
                  margin: 20px;
                  border-radius: 8px;
                }
                .fallback h3 {
                  color: #333;
                  margin-bottom: 20px;
                }
                .fallback p {
                  color: #666;
                  margin: 10px 0;
                }
                .download-btn {
                  display: inline-block;
                  background: #007bff;
                  color: white;
                  padding: 10px 20px;
                  text-decoration: none;
                  border-radius: 5px;
                  margin-top: 15px;
                  font-weight: bold;
                  transition: background 0.3s;
                }
                .download-btn:hover {
                  background: #0056b3;
                }
              </style>
            </head>
            <body>
              <div class="pdf-container">
                <div class="pdf-header">${titulo || "Documento PDF"}</div>
                <div class="loading" id="loading">Cargando PDF...</div>
                <object 
                  class="pdf-viewer" 
                  data="${src}" 
                  type="application/pdf"
                  style="display: none;"
                  onload="document.getElementById('loading').style.display='none'; this.style.display='block';">
                  <embed 
                    src="${src}" 
                    type="application/pdf" 
                    width="100%" 
                    height="100%" />
                  <div class="fallback">
                    <h3>No se puede mostrar el PDF</h3>
                    <p>Su navegador no soporta la visualización de PDFs integrada.</p>
                    <p>Puede descargar el archivo para verlo en una aplicación externa.</p>
                    <a class="download-btn" href="${src}" download="${titulo || "documento.pdf"}">Descargar PDF</a>
                  </div>
                </object>
              </div>
              <script>
                // Fallback si el objeto no carga después de 5 segundos
                setTimeout(function() {
                  var loading = document.getElementById('loading');
                  var object = document.querySelector('.pdf-viewer');
                  if (loading.style.display !== 'none') {
                    loading.style.display = 'none';
                    object.style.display = 'block';
                  }
                }, 5000);
              </script>
            </body>
          </html>
        `);
        ventana.document.close();
      }
    } else if (tipo === "img") {
      // Para imágenes, crear una página HTML simple con la imagen centrada
      const ventana = window.open("", "_blank");
      if (ventana) {
        ventana.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>${titulo || "Imagen"}</title>
              <meta charset="UTF-8">
              <style>
                body { 
                  margin: 0; 
                  padding: 20px; 
                  background: #f0f0f0; 
                  display: flex; 
                  justify-content: center; 
                  align-items: center; 
                  min-height: 100vh;
                  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                }
                .img-container {
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  max-width: 95vw;
                  max-height: 95vh;
                }
                .img-title {
                  background: rgba(0,0,0,0.8);
                  color: white;
                  padding: 10px 20px;
                  margin-bottom: 10px;
                  border-radius: 5px;
                  font-size: 14px;
                  text-align: center;
                }
                img { 
                  max-width: 100%; 
                  max-height: 80vh; 
                  object-fit: contain;
                  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
                  border-radius: 5px;
                  background: white;
                }
              </style>
            </head>
            <body>
              <div class="img-container">
                ${titulo ? `<div class="img-title">${titulo}</div>` : ""}
                <img src="${src}" alt="${titulo || "Imagen"}" />
              </div>
            </body>
          </html>
        `);
        ventana.document.close();
      }
    } else {
      // Para otros tipos, intentar abrir directamente
      window.open(src, "_blank");
    }
  };

  function parseBody(html: string): any {
    // Expresión regular para encontrar el contenido del cuerpo
    const bodyRegex = /<body[^>]*>((.|[\n\r])*)<\/body>/im;

    // Buscar el contenido del cuerpo usando la expresión regular
    const bodyMatches = html.match(bodyRegex);
    if (bodyMatches && bodyMatches.length > 1) {
      // Si se encuentra el contenido del cuerpo, devolverlo después de analizarlo
      const bodyContent = bodyMatches[1];
      return parse(bodyContent);
    } else {
      // Si no se encuentra el contenido del cuerpo, devolver null (o manejar el error según sea necesario)
      return null;
    }
  }

  const cleanQuestionMarks = (text: string): string => {
    if (!text) return text;

    let cleanedText = text;

    // Reparar cortes de palabra por símbolo "?" incrustado (ej: corazó?n)
    cleanedText = cleanedText.replace(
      /([A-Za-zÁÉÍÓÚáéíóúÑñÜü])\?([A-Za-zÁÉÍÓÚáéíóúÑñÜü])/g,
      "$1$2",
    );
    cleanedText = cleanedText.replace(
      /([áéíóúàèìòùâêîôûäëïöüñçÁÉÍÓÚÀÈÌÒÙÂÊÎÔÛÄËÏÖÜÑÇ])\?/g,
      "$1",
    );
    // Casos típicos de firma/líneas finales del informe: "?Dra", "?M.P.", "?M.E."
    cleanedText = cleanedText.replace(
      /\s+\?(?=\s*(Dra\.|Dr\.|M\.P\.|M\.E\.|Lic\.|Prof\.))/gi,
      "<br/>",
    );
    // Ruido antes de palabras (ej: "?colección")
    cleanedText = cleanedText.replace(
      /\s+\?(?=\s*[A-Za-zÁÉÍÓÚáéíóúÑñÜü])/g,
      " ",
    );
    cleanedText = cleanedText.replace(
      /(&(?:[a-zA-Z]+|#\d+|#x[0-9a-fA-F]+);)\?([A-Za-zÁ-ÿ])/g,
      "$1$2",
    );
    cleanedText = cleanedText.replace(/\?\s*\?\s*\?+/g, "<br/>");
    cleanedText = cleanedText.replace(/\?\s*\?/g, "<br/>");
    cleanedText = cleanedText.replace(/(^|[\s>])\?($|[\s<])/g, "$1<br/>$2");
    // Si una frase termina en ":" y luego empieza otra oración, forzar salto
    cleanedText = cleanedText.replace(/:(\s+)(?=[A-ZÁÉÍÓÚÑ])/g, ":<br/>");
    cleanedText = cleanedText.replace(/(<br\/>){3,}/g, "<br/><br/>");
    cleanedText = cleanedText.replace(/<br\/>\s+/g, "<br/>");
    cleanedText = cleanedText.replace(/\s+<br\/>/g, "<br/>");
    cleanedText = cleanedText.replace(/[ \t]{2,}/g, " ");
    return cleanedText;
  };

  const decodeRtfUnicode = (text: string): string => {
    return text.replace(/\\u(-?\d+)\s*\??/g, (_match, numStr) => {
      const code = Number(numStr);
      if (Number.isNaN(code)) return "";
      const normalized = code < 0 ? code + 65536 : code;
      try {
        return String.fromCharCode(normalized);
      } catch {
        return "";
      }
    });
  };

  const decodeCp1252Byte = (byte: number): string => {
    const cp1252Map: Record<number, string> = {
      0x80: "€",
      0x82: "‚",
      0x83: "ƒ",
      0x84: "„",
      0x85: "…",
      0x86: "†",
      0x87: "‡",
      0x88: "ˆ",
      0x89: "‰",
      0x8a: "Š",
      0x8b: "‹",
      0x8c: "Œ",
      0x8e: "Ž",
      0x91: "‘",
      0x92: "’",
      0x93: "“",
      0x94: "”",
      0x95: "•",
      0x96: "–",
      0x97: "—",
      0x98: "˜",
      0x99: "™",
      0x9a: "š",
      0x9b: "›",
      0x9c: "œ",
      0x9e: "ž",
      0x9f: "Ÿ",
    };
    if (cp1252Map[byte] !== undefined) return cp1252Map[byte];
    return String.fromCharCode(byte);
  };

  const decodeRtfHexEscapes = (text: string): string =>
    text.replace(/\\'([0-9a-fA-F]{2})/g, (_m, hex) =>
      decodeCp1252Byte(parseInt(hex, 16)),
    );

  const normalizeRtfEscapes = (text: string): string =>
    text.replace(/\\\\/g, "\\");

  const isPdfArrayBuffer = (buffer: ArrayBuffer): boolean => {
    if (!buffer || buffer.byteLength < 5) return false;
    const bytes = new Uint8Array(buffer.slice(0, 5));
    return (
      bytes[0] === 0x25 && // %
      bytes[1] === 0x50 && // P
      bytes[2] === 0x44 && // D
      bytes[3] === 0x46 && // F
      bytes[4] === 0x2d // -
    );
  };

  const looksLikeHtmlDocument = (text: string): boolean =>
    /<!doctype\s+html|<html\b|<head\b|<body\b|<\/html>/i.test(text || "");

  const sanitizeHtmlForViewer = (html: string): string => {
    if (!html) return html;
    let output = html;
    output = output.replace(/<style[\s\S]*?<\/style>/gi, "");
    output = output.replace(/text-align\s*:\s*[^;"']+/gi, "text-align:left");
    output = output.replace(/margin-left\s*:\s*[^;"']+;?/gi, "margin-left:0;");
    output = output.replace(
      /margin-right\s*:\s*[^;"']+;?/gi,
      "margin-right:0;",
    );
    output = output.replace(/text-indent\s*:\s*[^;"']+;?/gi, "text-indent:0;");
    output = output.replace(/\salign\s*=\s*["']?center["']?/gi, "");
    output = output.replace(/<center>/gi, "<div>");
    output = output.replace(/<\/center>/gi, "</div>");
    output = cleanQuestionMarks(output);

    const hasBreakTags = /<br\s*\/?>|<\/p>|<li\b/i.test(output);
    if (!hasBreakTags) {
      output = output.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
      output = output.replace(/\n{3,}/g, "\n\n");
      output = output.replace(/\n\n/g, "<br/><br/>");
      output = output.replace(/\n/g, "<br/>");
    }
    return output;
  };

  const hasBrokenAccentArtifacts = (text: string): boolean =>
    /([A-Za-zÁ-ÿ]|&(?:[a-zA-Z]+|#\d+|#x[0-9a-fA-F]+);)\?([A-Za-zÁ-ÿ]|&(?:[a-zA-Z]+|#\d+|#x[0-9a-fA-F]+);)/.test(
      text || "",
    );

  const trimRtfTail = (text: string): string => {
    const endStream = text.search(/\bendstream\b/i);
    const eof = text.search(/%%EOF/i);
    const endObj = text.search(/\bendobj\b/i);
    let end = text.length;
    if (endStream !== -1) end = Math.min(end, endStream);
    if (eof !== -1) end = Math.min(end, eof);
    if (endObj !== -1) end = Math.min(end, endObj);
    return text.slice(0, end).trim();
  };

  const extractRtfCandidate = (rawText: string): string | null => {
    const sanitized = rawText.replace(/\u0000/g, "");
    const sources = [sanitized, normalizeRtfEscapes(sanitized)];

    for (const source of sources) {
      if (/^\s*\{\\rtf/i.test(source)) {
        return source;
      }

      const startBlock = source.search(/\{\\pard\b/i);
      if (startBlock !== -1) {
        return trimRtfTail(source.slice(startBlock));
      }

      const startControl = source.search(/\\pard\b/i);
      if (startControl !== -1) {
        const fragment = trimRtfTail(source.slice(startControl));
        return fragment.startsWith("{") ? fragment : `{${fragment}}`;
      }

      if (/\\(pard|line|u-?\d+|tab)\b/i.test(source)) {
        return trimRtfTail(source);
      }
    }

    return null;
  };

  const fallbackRtfToHtml = (rtfText: string): string => {
    let text = rtfText;
    text = decodeRtfUnicode(text);
    text = decodeRtfHexEscapes(text);
    text = text.replace(/\\bullet\b/gi, "• ");
    text = text.replace(/\\par[d]?/gi, "<br/><br/>");
    text = text.replace(/\\line/gi, "<br/>");
    text = text.replace(/\\tab/gi, "    ");
    text = text.replace(/\\[a-z]+-?\d* ?/gi, "");
    text = text.replace(/[{}]/g, "");
    text = text.replace(/\u00a0/g, " ");
    text = text.replace(/\t/g, "    ");
    text = text.replace(/\r?\n/g, "<br/>");
    text = text.replace(/(<br\/>){3,}/g, "<br/><br/>");
    text = cleanQuestionMarks(text);
    text = text.replace(/(^|<br\/>)\s*[-•·◦]\s+/g, "$1• ");
    return text.trim();
  };

  const escapeHtml = (text: string): string =>
    text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const plainTextToHtml = (text: string): string | null => {
    if (!text) return null;
    const normalizedText = decodeRtfHexEscapes(
      decodeRtfUnicode(cleanQuestionMarks(text)),
    )
      .replace(/\u0000/g, "")
      .replace(/\u00a0/g, " ")
      .replace(/\t/g, "    ")
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .trim();
    if (!normalizedText) return null;

    if (looksLikeHtmlDocument(normalizedText)) {
      return normalizedText;
    }

    const alphanumericChars =
      normalizedText.match(/[A-Za-zÁ-ÿ0-9]/g)?.length || 0;
    if (normalizedText.length < 30 && alphanumericChars < 10) return null;

    const escaped = escapeHtml(normalizedText)
      .replace(/\n{3,}/g, "\n\n")
      .replace(/\n\n/g, "<br/><br/>")
      .replace(/\n/g, "<br/>");

    return `<div style="white-space:normal;font-family:Arial,sans-serif;line-height:1.5;">${escaped}</div>`;
  };

  const isLikelyReadableText = (text: string): boolean => {
    if (!text) return false;
    const compact = text.replace(/\s/g, "");
    if (compact.length < 80) return false;
    const printable =
      text.match(/[A-Za-zÁ-ÿ0-9\s.,;:()\-_/\\{}[\]'\"!?%]/g)?.length || 0;
    return printable / Math.max(text.length, 1) > 0.8;
  };

  const toArrayBuffer = async (data: any): Promise<ArrayBuffer | null> => {
    if (!data) return null;
    if (data instanceof ArrayBuffer) return data;
    if (ArrayBuffer.isView(data)) {
      // Crear una copia exacta del segmento visible para evitar problemas de tipado con buffer.slice
      return new Uint8Array(
        data.buffer,
        data.byteOffset,
        data.byteLength,
      ).slice().buffer;
    }
    if (data instanceof Blob) return await data.arrayBuffer();
    if (typeof data === "string") return new TextEncoder().encode(data).buffer;
    try {
      return new TextEncoder().encode(JSON.stringify(data)).buffer;
    } catch {
      return null;
    }
  };

  const extractTextFromPdfArrayBuffer = async (
    data: ArrayBuffer,
  ): Promise<string> => {
    let lastError: any = null;
    const modules: string[] = ["pdfjs-dist/legacy/build/pdf", "pdfjs-dist"];

    for (const modulePath of modules) {
      try {
        // @ts-ignore - carga dinámica para evitar incompatibilidades entre builds.
        const pdfModule = await import(modulePath);
        const pdfjsAny: any =
          pdfModule?.default &&
          typeof pdfModule.default.getDocument === "function"
            ? pdfModule.default
            : pdfModule;

        if (!pdfjsAny || typeof pdfjsAny.getDocument !== "function") {
          continue;
        }

        const loadingTask = pdfjsAny.getDocument({
          data,
          disableWorker: true,
        });
        const pdf = await loadingTask.promise;
        let fullText = "";

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
          const page = await pdf.getPage(pageNumber);
          const textContent = await page.getTextContent();
          const pageText = (textContent.items || [])
            .map((item: any) => item?.str || "")
            .join(" ");
          fullText += `${pageText}\n`;
        }

        return fullText.trim();
      } catch (error) {
        lastError = error;
      }
    }

    console.warn(
      "No se pudo extraer texto desde PDF para fallback RTF:",
      lastError,
    );
    return "";
  };

  const convertRtfTextToHtml = (rawText: string): string | null => {
    try {
      if (!rawText) {
        return null;
      }

      const rawNormalizado = decodeRtfHexEscapes(
        decodeRtfUnicode(normalizeRtfEscapes(rawText.replace(/\u0000/g, ""))),
      );
      const rtfCandidateRaw = extractRtfCandidate(rawNormalizado);
      if (!rtfCandidateRaw) {
        if (looksLikeHtmlDocument(rawNormalizado)) {
          return sanitizeHtmlForViewer(rawNormalizado);
        }
        return plainTextToHtml(rawNormalizado);
      }

      const rtfCandidate = normalizeRtfEscapes(rtfCandidateRaw);
      const cleanedRtfContent = cleanQuestionMarks(rtfCandidate);
      const isFullRtf = /^\s*\{\\rtf/i.test(cleanedRtfContent);
      const fallbackHtml = fallbackRtfToHtml(cleanedRtfContent);

      if (!isFullRtf) {
        return `<div style="white-space:normal;font-family:Arial,sans-serif;">${fallbackHtml}</div>`;
      }

      const htmlContent = convertRtfToHtml(cleanedRtfContent);
      if (!htmlContent) {
        return `<div style="white-space:normal;font-family:Arial,sans-serif;">${fallbackHtml}</div>`;
      }

      const htmlLimpioRaw = decodeRtfHexEscapes(
        decodeRtfUnicode(cleanQuestionMarks(htmlContent)),
      ).replace(/\u00a0/g, " ");
      const htmlLimpio = sanitizeHtmlForViewer(htmlLimpioRaw);

      const sourceHasLineBreakHints = /\\(line|par[d]?)/i.test(
        cleanedRtfContent,
      );
      const htmlHasLineBreaks = /<br\s*\/?>|<\/p>|<li\b/i.test(htmlLimpio);

      if (
        /\\(pard|line|u-?\d+)\b/i.test(htmlLimpio) ||
        hasBrokenAccentArtifacts(htmlLimpio) ||
        (sourceHasLineBreakHints && !htmlHasLineBreaks)
      ) {
        return `<div style="white-space:normal;font-family:Arial,sans-serif;">${fallbackHtml}</div>`;
      }
      return htmlLimpio;
    } catch (error) {
      console.error("Error convirtiendo RTF a HTML:", error);
      const fallbackHtml = fallbackRtfToHtml(rawText);
      if (fallbackHtml) {
        return `<div style="white-space:normal;font-family:Arial,sans-serif;">${fallbackHtml}</div>`;
      }
      return plainTextToHtml(rawText);
    }
  };

  const convertRtfBlobToHtml = async (blob: Blob): Promise<string | null> => {
    try {
      const rawText = await blob.text();
      return convertRtfTextToHtml(rawText);
    } catch (error) {
      console.error("Error leyendo blob de ecografía:", error);
      return null;
    }
  };

  /* Helper component to render pdf/img from different sources (base64, blob/url) */
  type RenderTipo = "pdf" | "img" | "other";
  interface RenderViewerProps {
    src?: string | null;
    tipo?: RenderTipo;
    isBase64?: boolean;
    title?: string;
    fallback?: string;
  }

  const RenderViewer: React.FC<RenderViewerProps> = ({
    src,
    tipo = "other",
    isBase64 = false,
    title,
    fallback,
  }) => {
    if (!src) {
      return (
        <IonText>
          {fallback || "No hay informe disponible para visualizar"}
        </IonText>
      );
    }

    if (tipo === "pdf") {
      const iframeSrc = isBase64 ? `data:application/pdf;base64,${src}` : src;
      return (
        <iframe
          src={iframeSrc}
          style={{ width: "100%", height: "100%", border: "none" }}
          title={title || "visor"}
        />
      );
    }

    if (tipo === "img") {
      return (
        <img
          src={src}
          alt={title || "imagen"}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      );
    }

    return (
      <IonText>Formato de archivo no compatible para visualización</IonText>
    );
  };

  // Funciones auxiliares para ecografías (ahora con archivos)
  const obtenerFechasUnicasEcografias = (): string[] => {
    const fechas = estudiosEcografiasArchivos
      .map((estudio) => {
        if (estudio.fecha) {
          return dayjs(estudio.fecha).format("DD/MM/YYYY");
        }
        return null;
      })
      .filter((fecha) => fecha !== null) as string[];
    // Eliminar duplicados y ordenar de más reciente a más antigua
    const fechasUnicas = Array.from(new Set(fechas));
    return fechasUnicas.sort((a, b) => {
      const fechaA = dayjs(a, "DD/MM/YYYY");
      const fechaB = dayjs(b, "DD/MM/YYYY");
      return fechaB.diff(fechaA); // Orden descendente (más reciente primero)
    });
  };

  const obtenerEcografiasPorFecha = (
    fechaSeleccionada: string,
  ): EstudiosPacientesArchivos[] => {
    return estudiosEcografiasArchivos.filter((estudio) => {
      if (estudio.fecha) {
        const fechaEstudio = dayjs(estudio.fecha).format("DD/MM/YYYY");
        return fechaEstudio === fechaSeleccionada;
      }
      return false;
    });
  };

  // Funciones auxiliares para histeroscopias
  // Funciones auxiliares para histeroscopias
  const obtenerFechasUnicasHisteroscopias = (): string[] => {
    const fechas = estudiosHisteroscopias
      .map((estudio) => {
        if (estudio.fecha) {
          return dayjs(estudio.fecha).format("DD/MM/YYYY");
        }
        return null;
      })
      .filter((fecha) => fecha !== null) as string[];

    // Eliminar duplicados y ordenar de más reciente a más antigua
    const fechasUnicas = Array.from(new Set(fechas));
    return fechasUnicas.sort((a, b) => {
      const fechaA = dayjs(a, "DD/MM/YYYY");
      const fechaB = dayjs(b, "DD/MM/YYYY");
      return fechaB.diff(fechaA); // Orden descendente (más reciente primero)
    });
  };

  const obtenerHisteroscopiasPorFecha = (
    fechaSeleccionada: string,
  ): EstudiosPacientesArchivos[] => {
    return estudiosHisteroscopias.filter((estudio) => {
      if (estudio.fecha) {
        const fechaEstudio = dayjs(estudio.fecha).format("DD/MM/YYYY");
        return fechaEstudio === fechaSeleccionada;
      }
      return false;
    });
  };

  // Funciones auxiliares para patología
  const obtenerFechasUnicasPatologia = (): string[] => {
    const fechas = estudiosPatologia
      .map((estudio) => {
        if (estudio.fecha) {
          return dayjs(estudio.fecha).format("DD/MM/YYYY");
        }
        return null;
      })
      .filter((fecha) => fecha !== null) as string[];

    // Eliminar duplicados y ordenar de más reciente a más antigua
    const fechasUnicas = Array.from(new Set(fechas));
    return fechasUnicas.sort((a, b) => {
      const fechaA = dayjs(a, "DD/MM/YYYY");
      const fechaB = dayjs(b, "DD/MM/YYYY");
      return fechaB.diff(fechaA); // Orden descendente (más reciente primero)
    });
  };

  const obtenerPatologiaPorFecha = (
    fechaSeleccionada: string,
  ): EstudiosPacientesPatologia[] => {
    return estudiosPatologia.filter((estudio) => {
      if (estudio.fecha) {
        const fechaEstudio = dayjs(estudio.fecha).format("DD/MM/YYYY");
        return fechaEstudio === fechaSeleccionada;
      }
      return false;
    });
  };

  const descargarEstudiosColposGeneralesPaciente = async (
    codigoDocumento: number,
    archivoSel: EstudiosPacientesArchivos,
  ) => {
    const config: AxiosRequestConfig = {
      headers: { Authorization: `Bearer ${hcUL?.token}` },
      responseType: "blob",
    };
    try {
      // carpeta=1 corresponde a Colpos-Generales
      const response = await http.get(
        `EstudiosPacientes/DescargarCospoImagenes?documentoNro=${codigoDocumento}&nombreArchivo=${archivoSel.nombre}&carpeta=1`,
        config,
      );

      const blob = response;
      if (blob && blob.size > 0) {
        // revocar preview anterior si existe
        if (archivoPreview && archivoPreview.url) {
          URL.revokeObjectURL(archivoPreview.url);
        }
        const fileURL = URL.createObjectURL(blob);
        const ext =
          (archivoSel.nombre || "").split(".").pop()?.toLowerCase() || "";
        const tipo =
          ext === "pdf"
            ? "pdf"
            : ["jpg", "jpeg", "png", "bmp", "gif"].includes(ext)
              ? "img"
              : "other";

        setArchivoPreview({ url: fileURL, tipo, nombre: archivoSel.nombre });
        setSelectedColposGenerales(archivoSel);
      }
    } catch (e) {
      console.error("Error descargando Colpos-Generales:", e);
    }
  };

  const descargarEstudiosHisteroscopiasPaciente = async (
    codigoDocumento: number,
    archivoSel: EstudiosPacientesArchivos,
  ) => {
    const config: AxiosRequestConfig = {
      headers: { Authorization: `Bearer ${hcUL?.token}` },
      responseType: "blob",
    };
    try {
      // carpeta=2 corresponde a Histeroscopias
      const response = await http.get(
        `EstudiosPacientes/DescargarCospoImagenes?documentoNro=${codigoDocumento}&nombreArchivo=${archivoSel.nombre}&carpeta=2`,
        config,
      );

      const blob = response;

      if (blob && blob.size > 0) {
        if (archivoPreview && archivoPreview.url) {
          URL.revokeObjectURL(archivoPreview.url);
        }
        const fileURL = URL.createObjectURL(blob);
        const ext =
          (archivoSel.nombre || "").split(".").pop()?.toLowerCase() || "";
        const tipo =
          ext === "pdf"
            ? "pdf"
            : ["jpg", "jpeg", "png", "bmp", "gif"].includes(ext)
              ? "img"
              : "other";

        setArchivoPreview({ url: fileURL, tipo, nombre: archivoSel.nombre });
        setSelectedHisteroscopias(archivoSel);
      } else {
        setSelectedHisteroscopias(archivoSel);
        setArchivoPreview(null); // Limpiar para forzar uso de URL directa
      }
    } catch (e) {
      console.error("Error descargando Histeroscopias:", e);
      setSelectedHisteroscopias(archivoSel);
      setArchivoPreview(null); // Limpiar para forzar uso de URL directa
    }
  };

  const descargarPatologia = async (patologia: EstudiosPacientesPatologia) => {
    const codigo = patologia.codigo;
    const config: AxiosRequestConfig = {
      headers: { Authorization: `Bearer ${hcUL?.token}` },
      responseType: "blob",
    };
    try {
      const response = await http.get(
        `EstudiosPacientes/DescargarPatologia?codigo=${codigo}`,
        config,
      );
      const blob = response;
      if (blob && blob.size > 0) {
        if (archivoPreview && archivoPreview.url) {
          URL.revokeObjectURL(archivoPreview.url);
        }
        const fileURL = URL.createObjectURL(blob);

        // Determinar el tipo de archivo basado en el nombre
        const ext =
          (patologia.nombre || "").split(".").pop()?.toLowerCase() || "";
        const tipo =
          ext === "pdf"
            ? "pdf"
            : ["jpg", "jpeg", "png", "bmp", "gif"].includes(ext)
              ? "img"
              : "other";

        setSelectedPatologia(patologia);
        setArchivoPreview({ url: fileURL, tipo, nombre: patologia.nombre });
      }
    } catch (e) {
      console.error("Error descargando Patologia por codigo:", e);
    }
  };

  return cargando ? (
    <div className="viewer-estudios-loading">
      <IonSpinner name="crescent" />
    </div>
  ) : (
    <div className="viewer-estudios-container">
      {estudiosEcografiasArchivos.length > 0 ||
      estudiosLaboratorio.length > 0 ||
      estudiosColposGenerales.length > 0 ||
      estudiosHisteroscopias.length > 0 ||
      estudiosPatologia.length > 0 ? (
        <>
          {/* Sección del Selector - Siempre visible */}
          <IonRow className="viewer-estudios-row">
            <IonCol
              sizeXs="12"
              sizeMd="4"
              sizeLg="3"
              className="viewer-estudios-sidebar"
            >
              <IonList className="viewer-estudios-list">
                <IonItem className="viewer-estudios-selector">
                  <IonSelect
                    label="Tipo de Estudio"
                    labelPlacement="floating"
                    interface="popover"
                    value={mostrarTreeview}
                    placeholder="Seleccionar tipo de estudio"
                    style={{ minWidth: "200px" }}
                    onIonChange={(e: any) => {
                      setMostrarTreeview(e.detail.value);
                      // Limpiar selecciones previas cuando cambia el tipo
                      setSelectedEcografia(null);
                      setSelectedFechaEcografia(null);
                      setSelectedLaboratorio(null);
                      setSelectedColposGenerales(null);
                      setSelectedHisteroscopias(null);
                      setSelectedFechaHisteroscopias(null);
                      setSelectedPatologia(null);
                      setSelectedFechaPatologia(null);
                      setArchivoPreview(null);
                      setEcografiaHtml(null);
                    }}
                  >
                    {estudiosEcografiasArchivos.length > 0 && (
                      <IonSelectOption value="Ecografias">
                        Ecografías
                      </IonSelectOption>
                    )}
                    {estudiosLaboratorio.length > 0 && (
                      <IonSelectOption value="Laboratorio">
                        Laboratorio
                      </IonSelectOption>
                    )}
                    {estudiosColposGenerales.length > 0 && (
                      <IonSelectOption value="Colpos-Generales">
                        Colpos-Generales
                      </IonSelectOption>
                    )}
                    {estudiosHisteroscopias.length > 0 && (
                      <IonSelectOption value="Histeroscopias">
                        Histeroscopias
                      </IonSelectOption>
                    )}
                    {estudiosPatologia.length > 0 && (
                      <IonSelectOption value="Patologia">
                        Patología
                      </IonSelectOption>
                    )}
                  </IonSelect>
                </IonItem>

                {!mostrarTreeview && (
                  <IonItem className="viewer-estudios-placeholder">
                    <IonText color="medium" style={{ fontSize: "14px" }}>
                      <p>
                        Seleccione un tipo de estudio del menú desplegable para
                        ver la lista disponible
                      </p>
                    </IonText>
                  </IonItem>
                )}

                {mostrarTreeview === "Ecografias" &&
                  !selectedFechaEcografia &&
                  obtenerFechasUnicasEcografias().map((fecha) => (
                    <IonItem
                      key={fecha}
                      button
                      onClick={() => setSelectedFechaEcografia(fecha)}
                      className="viewer-estudios-item"
                    >
                      <IonText style={{ fontSize: "14px" }}>📅 {fecha}</IonText>
                    </IonItem>
                  ))}

                {mostrarTreeview === "Ecografias" &&
                  selectedFechaEcografia &&
                  obtenerEcografiasPorFecha(selectedFechaEcografia).map(
                    (ecografia) => (
                      <IonItem
                        key={ecografia.nombre}
                        button
                        onClick={() =>
                          descargarEcografiaPaciente(
                            paciente.paciente.documentoNro || 0,
                            ecografia,
                          )
                        }
                        className={`viewer-estudios-item ${selectedEcografia?.nombre === ecografia.nombre ? "selected" : ""}`}
                      >
                        <IonText style={{ fontSize: "14px" }}>
                          📄{" "}
                          {ecografia.nombre.length > 24
                            ? ecografia.nombre.substring(0, 24) + "..."
                            : ecografia.nombre}
                        </IonText>
                      </IonItem>
                    ),
                  )}

                {mostrarTreeview === "Ecografias" && selectedFechaEcografia && (
                  <IonItem
                    button
                    onClick={() => {
                      setSelectedFechaEcografia(null);
                      setSelectedEcografia(null);
                      setEcografiaHtml(null);
                    }}
                    className="viewer-estudios-item"
                    style={{ borderTop: "1px solid #ddd", marginTop: "5px" }}
                  >
                    <IonText color="primary" style={{ fontSize: "14px" }}>
                      ← Volver a fechas
                    </IonText>
                  </IonItem>
                )}
                {mostrarTreeview === "Laboratorio" &&
                  estudiosLaboratorio.map((informe, index) => (
                    <IonItem
                      key={index}
                      button
                      onClick={() => setSelectedLaboratorio(informe)}
                      className={`viewer-estudios-item ${selectedLaboratorio === informe ? "selected" : ""}`}
                    >
                      <IonText style={{ fontSize: "14px" }}>
                        Informe - {informe.fecha}
                      </IonText>
                    </IonItem>
                  ))}
                {mostrarTreeview === "Colpos-Generales" &&
                  estudiosColposGenerales.map((colpos) => (
                    <IonItem
                      key={colpos.nombre}
                      button
                      onClick={() => {
                        descargarEstudiosColposGeneralesPaciente(
                          paciente.paciente.documentoNro || 0,
                          colpos,
                        );
                      }}
                      className={`viewer-estudios-item ${selectedColposGenerales?.nombre === colpos.nombre ? "selected" : ""}`}
                    >
                      <IonText style={{ fontSize: "14px" }}>
                        {colpos.nombre.length > 24
                          ? colpos.nombre.substring(0, 24) + "..."
                          : colpos.nombre}
                      </IonText>
                    </IonItem>
                  ))}
                {mostrarTreeview === "Histeroscopias" &&
                  !selectedFechaHisteroscopias &&
                  obtenerFechasUnicasHisteroscopias().map((fecha) => (
                    <IonItem
                      key={fecha}
                      button
                      onClick={() => setSelectedFechaHisteroscopias(fecha)}
                      className="viewer-estudios-item"
                    >
                      <IonText style={{ fontSize: "14px" }}>📅 {fecha}</IonText>
                    </IonItem>
                  ))}

                {mostrarTreeview === "Histeroscopias" &&
                  selectedFechaHisteroscopias &&
                  obtenerHisteroscopiasPorFecha(
                    selectedFechaHisteroscopias,
                  ).map((histeroscopia) => (
                    <IonItem
                      key={histeroscopia.nombre}
                      button
                      onClick={() => {
                        descargarEstudiosHisteroscopiasPaciente(
                          paciente.paciente.documentoNro || 0,
                          histeroscopia,
                        );
                      }}
                      className={`viewer-estudios-item ${selectedHisteroscopias?.nombre === histeroscopia.nombre ? "selected" : ""}`}
                    >
                      <IonText style={{ fontSize: "14px" }}>
                        📄{" "}
                        {histeroscopia.nombre.length > 24
                          ? histeroscopia.nombre.substring(0, 24) + "..."
                          : histeroscopia.nombre}
                      </IonText>
                    </IonItem>
                  ))}

                {mostrarTreeview === "Histeroscopias" &&
                  selectedFechaHisteroscopias && (
                    <IonItem
                      button
                      onClick={() => {
                        setSelectedFechaHisteroscopias(null);
                        setSelectedHisteroscopias(null);
                      }}
                      className="viewer-estudios-item"
                      style={{ borderTop: "1px solid #ddd", marginTop: "5px" }}
                    >
                      <IonText color="primary" style={{ fontSize: "14px" }}>
                        ← Volver a fechas
                      </IonText>
                    </IonItem>
                  )}
                {mostrarTreeview === "Patologia" &&
                  !selectedFechaPatologia &&
                  obtenerFechasUnicasPatologia().map((fecha) => (
                    <IonItem
                      key={fecha}
                      button
                      onClick={() => setSelectedFechaPatologia(fecha)}
                      className="viewer-estudios-item"
                    >
                      <IonText style={{ fontSize: "14px" }}>📅 {fecha}</IonText>
                    </IonItem>
                  ))}

                {mostrarTreeview === "Patologia" &&
                  selectedFechaPatologia &&
                  obtenerPatologiaPorFecha(selectedFechaPatologia).map(
                    (patologia) => (
                      <IonItem
                        key={patologia.codigo}
                        button
                        onClick={() => {
                          descargarPatologia(patologia);
                        }}
                        className={`viewer-estudios-item ${selectedPatologia?.codigo === patologia.codigo ? "selected" : ""}`}
                      >
                        <IonText style={{ fontSize: "14px" }}>
                          📄{" "}
                          {patologia.nombre.length > 24
                            ? patologia.nombre.substring(0, 24) + "..."
                            : patologia.nombre}
                        </IonText>
                      </IonItem>
                    ),
                  )}

                {mostrarTreeview === "Patologia" && selectedFechaPatologia && (
                  <IonItem
                    button
                    onClick={() => {
                      setSelectedFechaPatologia(null);
                      setSelectedPatologia(null);
                    }}
                    className="viewer-estudios-item"
                    style={{ borderTop: "1px solid #ddd", marginTop: "5px" }}
                  >
                    <IonText color="primary" style={{ fontSize: "14px" }}>
                      ← Volver a fechas
                    </IonText>
                  </IonItem>
                )}
              </IonList>
            </IonCol>

            {/* Columna de contenido - Solo en desktop/tablet */}
            <IonCol
              sizeXs="0"
              sizeMd="8"
              sizeLg="9"
              className="viewer-estudios-content desktop-only"
            >
              {!mostrarTreeview && (
                <div className="viewer-estudios-content-wrapper">
                  <div className="viewer-estudios-content-placeholder">
                    <IonText color="medium">
                      <h3>Visor de Estudios</h3>
                      <p>
                        Seleccione un tipo de estudio en el panel izquierdo para
                        comenzar a visualizar los documentos disponibles.
                      </p>
                    </IonText>
                  </div>
                </div>
              )}

              {mostrarTreeview === "Ecografias" && !selectedFechaEcografia && (
                <div className="viewer-estudios-content-wrapper">
                  <div className="viewer-estudios-content-placeholder">
                    <IonText color="medium">
                      <h3>Ecografías</h3>
                      <p>
                        Seleccione una fecha de la lista para ver los estudios
                        disponibles de ese día.
                      </p>
                    </IonText>
                  </div>
                </div>
              )}

              {mostrarTreeview === "Ecografias" &&
                selectedFechaEcografia &&
                !selectedEcografia && (
                  <div className="viewer-estudios-content-wrapper">
                    <div className="viewer-estudios-content-placeholder">
                      <IonText color="medium">
                        <h3>Ecografías - {selectedFechaEcografia}</h3>
                        <p>
                          Seleccione un estudio de la lista para ver su
                          contenido.
                        </p>
                      </IonText>
                    </div>
                  </div>
                )}

              {mostrarTreeview === "Laboratorio" && !selectedLaboratorio && (
                <div className="viewer-estudios-content-wrapper">
                  <div className="viewer-estudios-content-placeholder">
                    <IonText color="medium">
                      <h3>Laboratorio</h3>
                      <p>
                        Seleccione un informe de laboratorio de la lista para
                        ver su contenido.
                      </p>
                    </IonText>
                  </div>
                </div>
              )}

              {mostrarTreeview === "Colpos-Generales" &&
                !selectedColposGenerales && (
                  <div className="viewer-estudios-content-wrapper">
                    <div className="viewer-estudios-content-placeholder">
                      <IonText color="medium">
                        <h3>Colpos-Generales</h3>
                        <p>
                          Haga clic en un documento de la lista para verlo. 🔍
                          Los documentos se pueden ampliar haciendo clic en
                          ellos.
                        </p>
                      </IonText>
                    </div>
                  </div>
                )}

              {mostrarTreeview === "Histeroscopias" &&
                !selectedFechaHisteroscopias && (
                  <div className="viewer-estudios-content-wrapper">
                    <div className="viewer-estudios-content-placeholder">
                      <IonText color="medium">
                        <h3>Histeroscopias</h3>
                        <p>
                          Seleccione una fecha de la lista para ver los estudios
                          disponibles de ese día.
                        </p>
                      </IonText>
                    </div>
                  </div>
                )}

              {mostrarTreeview === "Histeroscopias" &&
                selectedFechaHisteroscopias &&
                !selectedHisteroscopias && (
                  <div className="viewer-estudios-content-wrapper">
                    <div className="viewer-estudios-content-placeholder">
                      <IonText color="medium">
                        <h3>Histeroscopias - {selectedFechaHisteroscopias}</h3>
                        <p>
                          Haga clic en un documento de la lista para verlo. 🔍
                          Los documentos se pueden ampliar haciendo clic en
                          ellos.
                        </p>
                      </IonText>
                    </div>
                  </div>
                )}

              {mostrarTreeview === "Patologia" && !selectedFechaPatologia && (
                <div className="viewer-estudios-content-wrapper">
                  <div className="viewer-estudios-content-placeholder">
                    <IonText color="medium">
                      <h3>Patología</h3>
                      <p>
                        Seleccione una fecha de la lista para ver los estudios
                        disponibles de ese día.
                      </p>
                    </IonText>
                  </div>
                </div>
              )}

              {mostrarTreeview === "Patologia" &&
                selectedFechaPatologia &&
                !selectedPatologia && (
                  <div className="viewer-estudios-content-wrapper">
                    <div className="viewer-estudios-content-placeholder">
                      <IonText color="medium">
                        <h3>Patología - {selectedFechaPatologia}</h3>
                        <p>
                          Haga clic en un documento de la lista para verlo. 🔍
                          Los documentos se pueden ampliar haciendo clic en
                          ellos.
                        </p>
                      </IonText>
                    </div>
                  </div>
                )}

              {mostrarTreeview === "Ecografias" && selectedEcografia && (
                <div className="viewer-estudios-content-wrapper">
                  {archivoPreview &&
                  archivoPreview.nombre === selectedEcografia.nombre ? (
                    archivoPreview.tipo === "pdf" ? (
                      <div className="viewer-estudios-clickable">
                        <iframe
                          src={archivoPreview.url}
                          className="viewer-estudios-iframe"
                          title={archivoPreview.nombre}
                        />
                        <div
                          className="viewer-estudios-zoom-indicator"
                          onClick={() =>
                            abrirEnNuevaVentana(
                              archivoPreview.url,
                              "pdf",
                              archivoPreview.nombre,
                            )
                          }
                          title="Click para ver a pantalla completa"
                        >
                          🔍 Click para ampliar
                        </div>
                      </div>
                    ) : archivoPreview.tipo === "img" ? (
                      <div className="viewer-estudios-clickable">
                        <img
                          src={archivoPreview.url}
                          alt={archivoPreview.nombre}
                          className="viewer-estudios-image"
                        />
                        <div
                          className="viewer-estudios-zoom-indicator"
                          onClick={() =>
                            abrirEnNuevaVentana(
                              archivoPreview.url,
                              "img",
                              archivoPreview.nombre,
                            )
                          }
                          title="Click para ver a pantalla completa"
                        >
                          🔍 Click para ampliar
                        </div>
                      </div>
                    ) : archivoPreview.tipo === "rtf" && ecografiaHtml ? (
                      <div className="viewer-estudios-content-wrapper viewer-estudios-text-content">
                        {parseBody(ecografiaHtml) || parse(ecografiaHtml)}
                      </div>
                    ) : (
                      <div className="viewer-estudios-no-content">
                        <IonText>
                          Formato de archivo no compatible para visualización
                        </IonText>
                      </div>
                    )
                  ) : (
                    <div className="viewer-estudios-no-content">
                      <IonText>
                        No se pudo cargar el estudio. Seleccione nuevamente el
                        archivo para reintentar.
                      </IonText>
                    </div>
                  )}
                </div>
              )}

              {mostrarTreeview === "Laboratorio" && selectedLaboratorio && (
                <div className="viewer-estudios-content-wrapper">
                  {selectedLaboratorio.extension === "pdf" &&
                  selectedLaboratorio.informeBase64 ? (
                    <div className="viewer-estudios-clickable">
                      <iframe
                        src={`data:application/pdf;base64,${selectedLaboratorio.informeBase64}`}
                        className="viewer-estudios-iframe"
                        title={`Informe de Laboratorio - ${selectedLaboratorio.fecha}`}
                      />
                      <div
                        className="viewer-estudios-zoom-indicator"
                        onClick={() =>
                          abrirEnNuevaVentana(
                            `data:application/pdf;base64,${selectedLaboratorio.informeBase64}`,
                            "pdf",
                            `Informe de Laboratorio - ${selectedLaboratorio.fecha}`,
                          )
                        }
                        title="Click para ver a pantalla completa"
                      >
                        🔍 Click para ampliar
                      </div>
                    </div>
                  ) : (
                    <div className="viewer-estudios-no-content">
                      <IonText>
                        No hay informe disponible para visualizar
                      </IonText>
                    </div>
                  )}
                </div>
              )}

              {mostrarTreeview === "Colpos-Generales" &&
                selectedColposGenerales && (
                  <div className="viewer-estudios-content-wrapper">
                    {archivoPreview &&
                    archivoPreview.nombre === selectedColposGenerales.nombre ? (
                      archivoPreview.tipo === "pdf" ? (
                        <div className="viewer-estudios-clickable">
                          <iframe
                            src={archivoPreview.url}
                            className="viewer-estudios-iframe"
                            title={archivoPreview.nombre}
                          />
                          <div
                            className="viewer-estudios-zoom-indicator"
                            onClick={() =>
                              abrirEnNuevaVentana(
                                archivoPreview.url,
                                "pdf",
                                archivoPreview.nombre,
                              )
                            }
                            title="Click para ver a pantalla completa"
                          >
                            🔍 Click para ampliar
                          </div>
                        </div>
                      ) : archivoPreview.tipo === "img" ? (
                        <div className="viewer-estudios-clickable">
                          <img
                            src={archivoPreview.url}
                            alt={archivoPreview.nombre}
                            className="viewer-estudios-image"
                          />
                          <div
                            className="viewer-estudios-zoom-indicator"
                            onClick={() =>
                              abrirEnNuevaVentana(
                                archivoPreview.url,
                                "img",
                                archivoPreview.nombre,
                              )
                            }
                            title="Click para ver a pantalla completa"
                          >
                            🔍 Click para ampliar
                          </div>
                        </div>
                      ) : (
                        <div className="viewer-estudios-no-content">
                          <IonText>
                            Formato de archivo no compatible para visualización
                          </IonText>
                        </div>
                      )
                    ) : selectedColposGenerales.nombre
                        .toLowerCase()
                        .endsWith(".pdf") ? (
                      <div
                        className="viewer-estudios-clickable"
                        onClick={() =>
                          abrirEnNuevaVentana(
                            `https://apihistorias.cgap.com.ar/api/v2/EstudiosPacientes/DescargarCospoImagenes?documentoNro=${paciente.paciente.documentoNro}&carpeta=1&nombreArchivo=${selectedColposGenerales.nombre}`,
                            "pdf",
                            selectedColposGenerales.nombre,
                          )
                        }
                        title="Click para ver a pantalla completa"
                      >
                        <iframe
                          src={`https://apihistorias.cgap.com.ar/api/v2/EstudiosPacientes/DescargarCospoImagenes?documentoNro=${paciente.paciente.documentoNro}&carpeta=1&nombreArchivo=${selectedColposGenerales.nombre}`}
                          className="viewer-estudios-iframe"
                          title={selectedColposGenerales.nombre}
                          style={{ pointerEvents: "none" }}
                        />
                        <div className="viewer-estudios-zoom-indicator">
                          🔍 Click para ampliar
                        </div>
                      </div>
                    ) : ["jpg", "jpeg", "png", "bmp", "gif"].some((ext) =>
                        selectedColposGenerales.nombre
                          .toLowerCase()
                          .endsWith(`.${ext}`),
                      ) ? (
                      <img
                        src={`https://apihistorias.cgap.com.ar/api/v2/EstudiosPacientes/DescargarCospoImagenes?documentoNro=${paciente.paciente.documentoNro}&carpeta=1&nombreArchivo=${selectedColposGenerales.nombre}`}
                        alt={selectedColposGenerales.nombre}
                        className="viewer-estudios-image viewer-estudios-clickable"
                        onClick={() =>
                          abrirEnNuevaVentana(
                            `https://apihistorias.cgap.com.ar/api/v2/EstudiosPacientes/DescargarCospoImagenes?documentoNro=${paciente.paciente.documentoNro}&carpeta=1&nombreArchivo=${selectedColposGenerales.nombre}`,
                            "img",
                            selectedColposGenerales.nombre,
                          )
                        }
                        title="Click para ver a pantalla completa"
                      />
                    ) : (
                      <div className="viewer-estudios-no-content">
                        <IonText>
                          Formato de archivo no compatible para visualización
                        </IonText>
                      </div>
                    )}
                  </div>
                )}

              {mostrarTreeview === "Histeroscopias" &&
                selectedHisteroscopias && (
                  <div className="viewer-estudios-content-wrapper">
                    {archivoPreview &&
                    archivoPreview.nombre === selectedHisteroscopias.nombre ? (
                      archivoPreview.tipo === "pdf" ? (
                        <div className="viewer-estudios-clickable">
                          <iframe
                            src={archivoPreview.url}
                            className="viewer-estudios-iframe"
                            title={archivoPreview.nombre}
                          />
                          <div
                            className="viewer-estudios-zoom-indicator"
                            onClick={() =>
                              abrirEnNuevaVentana(
                                archivoPreview.url,
                                "pdf",
                                archivoPreview.nombre,
                              )
                            }
                            title="Click para ver a pantalla completa"
                          >
                            🔍 Click para ampliar
                          </div>
                        </div>
                      ) : archivoPreview.tipo === "img" ? (
                        <div className="viewer-estudios-clickable">
                          <img
                            src={archivoPreview.url}
                            alt={archivoPreview.nombre}
                            className="viewer-estudios-image"
                          />
                          <div
                            className="viewer-estudios-zoom-indicator"
                            onClick={() =>
                              abrirEnNuevaVentana(
                                archivoPreview.url,
                                "img",
                                archivoPreview.nombre,
                              )
                            }
                            title="Click para ver a pantalla completa"
                          >
                            🔍 Click para ampliar
                          </div>
                        </div>
                      ) : (
                        <div className="viewer-estudios-no-content">
                          <IonText>
                            Formato de archivo no compatible para visualización
                          </IonText>
                        </div>
                      )
                    ) : selectedHisteroscopias.nombre
                        .toLowerCase()
                        .endsWith(".pdf") ? (
                      <div
                        style={{ cursor: "pointer", position: "relative" }}
                        onClick={() =>
                          abrirEnNuevaVentana(
                            `https://apihistorias.cgap.com.ar/api/v2/EstudiosPacientes/DescargarCospoImagenes?documentoNro=${paciente.paciente.documentoNro}&carpeta=2&nombreArchivo=${selectedHisteroscopias.nombre}`,
                            "pdf",
                            selectedHisteroscopias.nombre,
                          )
                        }
                        title="Click para ver a pantalla completa"
                      >
                        <iframe
                          src={`https://apihistorias.cgap.com.ar/api/v2/EstudiosPacientes/DescargarCospoImagenes?documentoNro=${paciente.paciente.documentoNro}&carpeta=2&nombreArchivo=${selectedHisteroscopias.nombre}`}
                          className="viewer-estudios-iframe"
                          title={selectedHisteroscopias.nombre}
                          style={{ pointerEvents: "none" }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: "10px",
                            right: "10px",
                            background: "rgba(0,0,0,0.7)",
                            color: "white",
                            padding: "5px 10px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            zIndex: 1,
                          }}
                        >
                          🔍 Click para ampliar
                        </div>
                      </div>
                    ) : ["jpg", "jpeg", "png", "bmp", "gif"].some((ext) =>
                        selectedHisteroscopias.nombre
                          .toLowerCase()
                          .endsWith(`.${ext}`),
                      ) ? (
                      <img
                        src={`https://apihistorias.cgap.com.ar/api/v2/EstudiosPacientes/DescargarCospoImagenes?documentoNro=${paciente.paciente.documentoNro}&carpeta=2&nombreArchivo=${selectedHisteroscopias.nombre}`}
                        alt={selectedHisteroscopias.nombre}
                        className="viewer-estudios-image"
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          abrirEnNuevaVentana(
                            `https://apihistorias.cgap.com.ar/api/v2/EstudiosPacientes/DescargarCospoImagenes?documentoNro=${paciente.paciente.documentoNro}&carpeta=2&nombreArchivo=${selectedHisteroscopias.nombre}`,
                            "img",
                            selectedHisteroscopias.nombre,
                          )
                        }
                        title="Click para ver a pantalla completa"
                      />
                    ) : (
                      <div className="viewer-estudios-no-content">
                        <IonText>
                          Formato de archivo no compatible para visualización
                        </IonText>
                      </div>
                    )}
                  </div>
                )}

              {mostrarTreeview === "Patologia" && selectedPatologia && (
                <div className="viewer-estudios-content-wrapper">
                  {archivoPreview &&
                  archivoPreview.nombre === selectedPatologia.nombre ? (
                    archivoPreview.tipo === "pdf" ? (
                      <div className="viewer-estudios-clickable">
                        <iframe
                          src={archivoPreview.url}
                          className="viewer-estudios-iframe"
                          title={selectedPatologia.nombre}
                          style={{ pointerEvents: "none" }}
                        />
                        <div
                          className="viewer-estudios-zoom-indicator"
                          onClick={() =>
                            abrirEnNuevaVentana(
                              archivoPreview.url,
                              "pdf",
                              selectedPatologia.nombre,
                            )
                          }
                          title="Click para ver a pantalla completa"
                        >
                          🔍 Click para ampliar
                        </div>
                      </div>
                    ) : archivoPreview.tipo === "img" ? (
                      <img
                        src={archivoPreview.url}
                        alt={selectedPatologia.nombre}
                        className="viewer-estudios-image viewer-estudios-clickable"
                        onClick={() =>
                          abrirEnNuevaVentana(
                            archivoPreview.url,
                            "img",
                            selectedPatologia.nombre,
                          )
                        }
                        title="Click para ver a pantalla completa"
                      />
                    ) : (
                      <div className="viewer-estudios-no-content">
                        <IonText>
                          Formato de archivo no compatible para visualización
                        </IonText>
                      </div>
                    )
                  ) : (
                    <div className="viewer-estudios-no-content">
                      <IonText>
                        No hay informe de patología disponible para visualizar
                      </IonText>
                    </div>
                  )}
                </div>
              )}

              {!selectedEcografia &&
                !selectedLaboratorio &&
                !selectedColposGenerales &&
                !selectedHisteroscopias &&
                !selectedPatologia && (
                  <div className="viewer-estudios-no-content">
                    <IonText>
                      Seleccione un estudio de la lista para visualizarlo
                    </IonText>
                  </div>
                )}
            </IonCol>
          </IonRow>

          {/* Sección móvil - Solo visible en pantallas pequeñas */}
          {isMobile && (
            <div
              className="mobile-content-section"
              style={{
                display: "block",
                width: "100%",
                margin: "0",
                marginTop: "0px",
                padding: "5px",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
                border: "1px solid #ddd",
                position: "relative",
                zIndex: 999,
              }}
            >
              {!mostrarTreeview && (
                <div
                  className="viewer-estudios-content-wrapper"
                  style={{
                    minHeight: "45vh",
                    maxHeight: "55vh",
                    overflow: "auto",
                    width: "100%",
                    margin: "0",
                    padding: "5px",
                  }}
                >
                  <div className="viewer-estudios-content-placeholder">
                    <IonText color="medium">
                      <h3>Visor de Estudios</h3>
                      <p>
                        Seleccione un tipo de estudio en el panel de arriba para
                        comenzar a visualizar los documentos disponibles.
                      </p>
                    </IonText>
                  </div>
                </div>
              )}

              {mostrarTreeview === "Ecografias" && !selectedFechaEcografia && (
                <div
                  className="viewer-estudios-content-wrapper"
                  style={{
                    minHeight: "45vh",
                    maxHeight: "55vh",
                    overflow: "auto",
                    width: "100%",
                    margin: "0",
                    padding: "5px",
                  }}
                >
                  <div className="viewer-estudios-content-placeholder">
                    <IonText color="medium">
                      <h3>Ecografías</h3>
                      <p>
                        Seleccione una fecha de la lista para ver los estudios
                        disponibles.
                      </p>
                    </IonText>
                  </div>
                </div>
              )}

              {mostrarTreeview === "Ecografias" &&
                selectedFechaEcografia &&
                !selectedEcografia && (
                  <div
                    className="viewer-estudios-content-wrapper"
                    style={{
                      minHeight: "45vh",
                      maxHeight: "55vh",
                      overflow: "auto",
                      width: "100%",
                      margin: "0",
                      padding: "5px",
                    }}
                  >
                    <div className="viewer-estudios-content-placeholder">
                      <IonText color="medium">
                        <h3>Ecografías - {selectedFechaEcografia}</h3>
                        <p>
                          Seleccione un estudio de la lista para ver su
                          contenido.
                        </p>
                      </IonText>
                    </div>
                  </div>
                )}

              {mostrarTreeview === "Laboratorio" && !selectedLaboratorio && (
                <div
                  className="viewer-estudios-content-wrapper"
                  style={{
                    minHeight: "45vh",
                    maxHeight: "55vh",
                    overflow: "auto",
                    width: "100%",
                    margin: "0",
                    padding: "5px",
                  }}
                >
                  <div className="viewer-estudios-content-placeholder">
                    <IonText color="medium">
                      <h3>Laboratorio</h3>
                      <p>
                        Seleccione un informe de laboratorio de la lista para
                        ver su contenido.
                      </p>
                    </IonText>
                  </div>
                </div>
              )}

              {mostrarTreeview === "Colpos-Generales" &&
                !selectedColposGenerales && (
                  <div
                    className="viewer-estudios-content-wrapper"
                    style={{
                      minHeight: "45vh",
                      maxHeight: "55vh",
                      overflow: "auto",
                      width: "100%",
                      margin: "0",
                      padding: "5px",
                    }}
                  >
                    <div className="viewer-estudios-content-placeholder">
                      <IonText color="medium">
                        <h3>Colpos-Generales</h3>
                        <p>
                          Haga clic en un documento de la lista para verlo. 🔍
                          Los documentos se pueden ampliar haciendo clic en
                          ellos.
                        </p>
                      </IonText>
                    </div>
                  </div>
                )}

              {mostrarTreeview === "Histeroscopias" &&
                !selectedFechaHisteroscopias && (
                  <div
                    className="viewer-estudios-content-wrapper"
                    style={{
                      minHeight: "45vh",
                      maxHeight: "55vh",
                      overflow: "auto",
                      width: "100%",
                      margin: "0",
                      padding: "5px",
                    }}
                  >
                    <div className="viewer-estudios-content-placeholder">
                      <IonText color="medium">
                        <h3>Histeroscopias</h3>
                        <p>
                          Seleccione una fecha de la lista para ver los estudios
                          disponibles.
                        </p>
                      </IonText>
                    </div>
                  </div>
                )}

              {mostrarTreeview === "Histeroscopias" &&
                selectedFechaHisteroscopias &&
                !selectedHisteroscopias && (
                  <div
                    className="viewer-estudios-content-wrapper"
                    style={{
                      minHeight: "45vh",
                      maxHeight: "55vh",
                      overflow: "auto",
                      width: "100%",
                      margin: "0",
                      padding: "5px",
                    }}
                  >
                    <div className="viewer-estudios-content-placeholder">
                      <IonText color="medium">
                        <h3>Histeroscopias - {selectedFechaHisteroscopias}</h3>
                        <p>Haga clic en un documento de la lista para verlo.</p>
                      </IonText>
                    </div>
                  </div>
                )}

              {mostrarTreeview === "Patologia" && !selectedFechaPatologia && (
                <div
                  className="viewer-estudios-content-wrapper"
                  style={{
                    minHeight: "45vh",
                    maxHeight: "55vh",
                    overflow: "auto",
                    width: "100%",
                    margin: "0",
                    padding: "5px",
                  }}
                >
                  <div className="viewer-estudios-content-placeholder">
                    <IonText color="medium">
                      <h3>Patología</h3>
                      <p>
                        Seleccione una fecha de la lista para ver los estudios
                        disponibles.
                      </p>
                    </IonText>
                  </div>
                </div>
              )}

              {mostrarTreeview === "Patologia" &&
                selectedFechaPatologia &&
                !selectedPatologia && (
                  <div
                    className="viewer-estudios-content-wrapper"
                    style={{
                      minHeight: "45vh",
                      maxHeight: "55vh",
                      overflow: "auto",
                      width: "100%",
                      margin: "0",
                      padding: "5px",
                    }}
                  >
                    <div className="viewer-estudios-content-placeholder">
                      <IonText color="medium">
                        <h3>Patología - {selectedFechaPatologia}</h3>
                        <p>Haga clic en un documento de la lista para verlo.</p>
                      </IonText>
                    </div>
                  </div>
                )}

              {mostrarTreeview === "Ecografias" && selectedEcografia && (
                <div
                  className="viewer-estudios-content-wrapper"
                  style={{
                    minHeight: "45vh",
                    maxHeight: "55vh",
                    overflow: "auto",
                    width: "100%",
                    margin: "0",
                    padding: "5px",
                  }}
                >
                  {archivoPreview &&
                  archivoPreview.nombre === selectedEcografia.nombre ? (
                    archivoPreview.tipo === "pdf" ? (
                      <div className="viewer-estudios-clickable">
                        <object
                          data={archivoPreview.url}
                          type="application/pdf"
                          className="viewer-estudios-pdf-object"
                          title={selectedEcografia.nombre}
                        >
                          <embed
                            src={archivoPreview.url}
                            type="application/pdf"
                          />
                          <div className="viewer-estudios-no-content">
                            <IonText>
                              No se puede mostrar el PDF en este dispositivo.
                            </IonText>
                            <span
                              className="viewer-estudios-open-link"
                              onClick={() =>
                                abrirEnNuevaVentana(
                                  archivoPreview.url,
                                  "pdf",
                                  selectedEcografia.nombre,
                                )
                              }
                            >
                              Abrir PDF
                            </span>
                          </div>
                        </object>
                        <div
                          className="viewer-estudios-zoom-indicator"
                          onClick={() =>
                            abrirEnNuevaVentana(
                              archivoPreview.url,
                              "pdf",
                              selectedEcografia.nombre,
                            )
                          }
                          title="Click para ver a pantalla completa"
                        >
                          🔍 Click para ampliar
                        </div>
                      </div>
                    ) : archivoPreview.tipo === "img" ? (
                      <div className="viewer-estudios-clickable">
                        <img
                          src={archivoPreview.url}
                          alt={selectedEcografia.nombre}
                          className="viewer-estudios-image"
                        />
                        <div
                          className="viewer-estudios-zoom-indicator"
                          onClick={() =>
                            abrirEnNuevaVentana(
                              archivoPreview.url,
                              "img",
                              selectedEcografia.nombre,
                            )
                          }
                          title="Click para ver a pantalla completa"
                        >
                          🔍 Click para ampliar
                        </div>
                      </div>
                    ) : archivoPreview.tipo === "rtf" && ecografiaHtml ? (
                      <div className="viewer-estudios-content-wrapper viewer-estudios-text-content">
                        {parseBody(ecografiaHtml) || parse(ecografiaHtml)}
                      </div>
                    ) : (
                      <div className="viewer-estudios-no-content">
                        <IonText>
                          Formato de archivo no compatible para visualización
                        </IonText>
                      </div>
                    )
                  ) : (
                    <div className="viewer-estudios-no-content">
                      <IonText>
                        No se pudo cargar el estudio. Seleccione nuevamente el
                        archivo para reintentar.
                      </IonText>
                    </div>
                  )}
                </div>
              )}

              {mostrarTreeview === "Laboratorio" && selectedLaboratorio && (
                <div
                  className="viewer-estudios-content-wrapper"
                  style={{
                    minHeight: "45vh",
                    maxHeight: "55vh",
                    overflow: "auto",
                    width: "100%",
                    margin: "0",
                    padding: "5px",
                  }}
                >
                  {selectedLaboratorio.extension === "pdf" &&
                  selectedLaboratorio.informeBase64 ? (
                    <div className="viewer-estudios-clickable">
                      <object
                        data={`data:application/pdf;base64,${selectedLaboratorio.informeBase64}`}
                        type="application/pdf"
                        className="viewer-estudios-pdf-object"
                        title={`Informe de Laboratorio - ${selectedLaboratorio.fecha}`}
                      >
                        <embed
                          src={`data:application/pdf;base64,${selectedLaboratorio.informeBase64}`}
                          type="application/pdf"
                        />
                        <div className="viewer-estudios-no-content">
                          <IonText>
                            No se puede mostrar el PDF en este dispositivo.
                          </IonText>
                          <span
                            className="viewer-estudios-open-link"
                            onClick={() =>
                              abrirEnNuevaVentana(
                                `data:application/pdf;base64,${selectedLaboratorio.informeBase64}`,
                                "pdf",
                                `Informe de Laboratorio - ${selectedLaboratorio.fecha}`,
                              )
                            }
                          >
                            Abrir PDF
                          </span>
                        </div>
                      </object>
                      <div
                        className="viewer-estudios-zoom-indicator"
                        onClick={() =>
                          abrirEnNuevaVentana(
                            `data:application/pdf;base64,${selectedLaboratorio.informeBase64}`,
                            "pdf",
                            `Informe de Laboratorio - ${selectedLaboratorio.fecha}`,
                          )
                        }
                        title="Click para ver a pantalla completa"
                      >
                        🔍 Click para ampliar
                      </div>
                    </div>
                  ) : (
                    <div className="viewer-estudios-no-content">
                      <IonText>
                        No hay informe disponible para visualizar
                      </IonText>
                    </div>
                  )}
                </div>
              )}

              {mostrarTreeview === "Colpos-Generales" &&
                selectedColposGenerales && (
                  <div
                    className="viewer-estudios-content-wrapper"
                    style={{
                      minHeight: "45vh",
                      maxHeight: "55vh",
                      overflow: "auto",
                      width: "100%",
                      margin: "0",
                      padding: "5px",
                    }}
                  >
                    {archivoPreview &&
                    archivoPreview.nombre === selectedColposGenerales.nombre ? (
                      archivoPreview.tipo === "pdf" ? (
                        <div className="viewer-estudios-clickable">
                          <object
                            data={archivoPreview.url}
                            type="application/pdf"
                            className="viewer-estudios-pdf-object"
                            title={archivoPreview.nombre}
                          >
                            <embed
                              src={archivoPreview.url}
                              type="application/pdf"
                            />
                            <div className="viewer-estudios-no-content">
                              <IonText>
                                No se puede mostrar el PDF en este dispositivo.
                              </IonText>
                              <span
                                className="viewer-estudios-open-link"
                                onClick={() =>
                                  abrirEnNuevaVentana(
                                    archivoPreview.url,
                                    "pdf",
                                    archivoPreview.nombre,
                                  )
                                }
                              >
                                Abrir PDF
                              </span>
                            </div>
                          </object>
                          <div
                            className="viewer-estudios-zoom-indicator"
                            onClick={() =>
                              abrirEnNuevaVentana(
                                archivoPreview.url,
                                "pdf",
                                archivoPreview.nombre,
                              )
                            }
                            title="Click para ver a pantalla completa"
                          >
                            🔍 Click para ampliar
                          </div>
                        </div>
                      ) : archivoPreview.tipo === "img" ? (
                        <div className="viewer-estudios-clickable">
                          <img
                            src={archivoPreview.url}
                            alt={archivoPreview.nombre}
                            className="viewer-estudios-image"
                          />
                          <div
                            className="viewer-estudios-zoom-indicator"
                            onClick={() =>
                              abrirEnNuevaVentana(
                                archivoPreview.url,
                                "img",
                                archivoPreview.nombre,
                              )
                            }
                            title="Click para ver a pantalla completa"
                          >
                            🔍 Click para ampliar
                          </div>
                        </div>
                      ) : (
                        <div className="viewer-estudios-no-content">
                          <IonText>
                            Formato de archivo no compatible para visualización
                          </IonText>
                        </div>
                      )
                    ) : (
                      <div className="viewer-estudios-no-content">
                        <IonText>
                          No hay archivo disponible para visualizar
                        </IonText>
                      </div>
                    )}
                  </div>
                )}

              {mostrarTreeview === "Histeroscopias" &&
                selectedHisteroscopias && (
                  <div
                    className="viewer-estudios-content-wrapper"
                    style={{
                      minHeight: "45vh",
                      maxHeight: "55vh",
                      overflow: "auto",
                      width: "100%",
                      margin: "0",
                      padding: "5px",
                    }}
                  >
                    {archivoPreview &&
                    archivoPreview.nombre === selectedHisteroscopias.nombre ? (
                      archivoPreview.tipo === "pdf" ? (
                        <div className="viewer-estudios-clickable">
                          <object
                            data={archivoPreview.url}
                            type="application/pdf"
                            className="viewer-estudios-pdf-object"
                            title={archivoPreview.nombre}
                          >
                            <embed
                              src={archivoPreview.url}
                              type="application/pdf"
                            />
                            <div className="viewer-estudios-no-content">
                              <IonText>
                                No se puede mostrar el PDF en este dispositivo.
                              </IonText>
                              <span
                                className="viewer-estudios-open-link"
                                onClick={() =>
                                  abrirEnNuevaVentana(
                                    archivoPreview.url,
                                    "pdf",
                                    archivoPreview.nombre,
                                  )
                                }
                              >
                                Abrir PDF
                              </span>
                            </div>
                          </object>
                          <div
                            className="viewer-estudios-zoom-indicator"
                            onClick={() =>
                              abrirEnNuevaVentana(
                                archivoPreview.url,
                                "pdf",
                                archivoPreview.nombre,
                              )
                            }
                            title="Click para ver a pantalla completa"
                          >
                            🔍 Click para ampliar
                          </div>
                        </div>
                      ) : archivoPreview.tipo === "img" ? (
                        <div className="viewer-estudios-clickable">
                          <img
                            src={archivoPreview.url}
                            alt={archivoPreview.nombre}
                            className="viewer-estudios-image"
                          />
                          <div
                            className="viewer-estudios-zoom-indicator"
                            onClick={() =>
                              abrirEnNuevaVentana(
                                archivoPreview.url,
                                "img",
                                archivoPreview.nombre,
                              )
                            }
                            title="Click para ver a pantalla completa"
                          >
                            🔍 Click para ampliar
                          </div>
                        </div>
                      ) : (
                        <div className="viewer-estudios-no-content">
                          <IonText>
                            Formato de archivo no compatible para visualización
                          </IonText>
                        </div>
                      )
                    ) : (
                      <div className="viewer-estudios-no-content">
                        <IonText>
                          No hay archivo disponible para visualizar
                        </IonText>
                      </div>
                    )}
                  </div>
                )}

              {mostrarTreeview === "Patologia" && selectedPatologia && (
                <div
                  className="viewer-estudios-content-wrapper"
                  style={{
                    minHeight: "45vh",
                    maxHeight: "55vh",
                    overflow: "auto",
                    width: "100%",
                    margin: "0",
                    padding: "5px",
                  }}
                >
                  {archivoPreview &&
                  archivoPreview.nombre === selectedPatologia.nombre ? (
                    archivoPreview.tipo === "pdf" ? (
                      <div className="viewer-estudios-clickable">
                        <object
                          data={archivoPreview.url}
                          type="application/pdf"
                          className="viewer-estudios-pdf-object"
                          title={selectedPatologia.nombre}
                        >
                          <embed
                            src={archivoPreview.url}
                            type="application/pdf"
                          />
                          <div className="viewer-estudios-no-content">
                            <IonText>
                              No se puede mostrar el PDF en este dispositivo.
                            </IonText>
                            <span
                              className="viewer-estudios-open-link"
                              onClick={() =>
                                abrirEnNuevaVentana(
                                  archivoPreview.url,
                                  "pdf",
                                  selectedPatologia.nombre,
                                )
                              }
                            >
                              Abrir PDF
                            </span>
                          </div>
                        </object>
                        <div
                          className="viewer-estudios-zoom-indicator"
                          onClick={() =>
                            abrirEnNuevaVentana(
                              archivoPreview.url,
                              "pdf",
                              selectedPatologia.nombre,
                            )
                          }
                          title="Click para ver a pantalla completa"
                        >
                          🔍 Click para ampliar
                        </div>
                      </div>
                    ) : archivoPreview.tipo === "img" ? (
                      <img
                        src={archivoPreview.url}
                        alt={selectedPatologia.nombre}
                        className="viewer-estudios-image"
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          abrirEnNuevaVentana(
                            archivoPreview.url,
                            "img",
                            selectedPatologia.nombre,
                          )
                        }
                        title="Click para ver a pantalla completa"
                      />
                    ) : (
                      <div className="viewer-estudios-no-content">
                        <IonText>
                          Formato de archivo no compatible para visualización
                        </IonText>
                      </div>
                    )
                  ) : (
                    <div className="viewer-estudios-no-content">
                      <IonText>
                        No hay informe de patología disponible para visualizar
                      </IonText>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <IonItem>
          <IonText>No se encontraron estudios registrados</IonText>
        </IonItem>
      )}
    </div>
  );
};
export default ViewerEstudiosPaciente;
